import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'

const AuthContext = createContext(null)

const API = `${process.env.REACT_APP_BACKEND_URL}/api`
const ADMIN_EMAIL = "jonhnyssabino@gmail.com"
const ALL_MODULES = ["dashboard", "processos", "licitacoes", "especialistas", "abastecimento", "atendimentos"]

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(localStorage.getItem('token'))
  const [loading, setLoading] = useState(true)

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setUser(response.data)
    } catch {
      localStorage.removeItem('token')
      setToken(null)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      fetchUser()
    } else {
      delete axios.defaults.headers.common['Authorization']
      setLoading(false)
    }
  }, [token, fetchUser])

  const login = useCallback(async (email, senha) => {
    const response = await axios.post(`${API}/auth/login`, { email, senha })
    const { access_token, user: userData } = response.data
    localStorage.setItem('token', access_token)
    setToken(access_token)
    setUser(userData)
    return userData
  }, [])

  const register = useCallback(async (nome, email, senha, cargo) => {
    const response = await axios.post(`${API}/auth/register`, { nome, email, senha, cargo })
    const { access_token, user: userData } = response.data
    localStorage.setItem('token', access_token)
    setToken(access_token)
    setUser(userData)
    return userData
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('token')
    delete axios.defaults.headers.common['Authorization']
    setToken(null)
    setUser(null)
  }, [])

  const isAdmin = user?.is_admin || user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()
  const userPermissions = isAdmin ? ALL_MODULES : (user?.permissoes || [])

  const hasPermission = useCallback((module) => {
    if (isAdmin) return true
    return userPermissions.includes(module)
  }, [isAdmin, userPermissions])

  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin,
    userPermissions,
    hasPermission
  }), [user, token, loading, login, register, logout, isAdmin, userPermissions, hasPermission])

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
